import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * <TagsInput value={["a","b"]} onChange={(arr) => ...} placeholder="..." />
 *
 * Fixes the global bug where a raw `e.target.value.split(',')` onChange
 * stripped trailing commas and prevented users from typing multi-word tags
 * or pasting comma lists. Internally tracks the raw string and only emits
 * an updated array on blur / Enter / explicit comma commit.
 */
export default function TagsInput({
  value,
  onChange,
  placeholder = "Add tag and press Enter",
  className = "",
  testId,
}) {
  const tags = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const ref = useRef(null);

  const commit = (raw) => {
    const next = raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (next.length === 0) return;
    const merged = [...tags];
    for (const t of next) {
      if (!merged.includes(t)) merged.push(t);
    }
    onChange(merged);
    setDraft("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
    } else if (e.key === "," ) {
      e.preventDefault();
      if (draft.trim()) commit(draft);
    } else if (e.key === "Backspace" && draft === "" && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };

  const remove = (idx) => onChange(tags.filter((_, i) => i !== idx));

  return (
    <div
      className={`kb-input flex flex-wrap items-center gap-1.5 cursor-text ${className}`}
      onClick={() => ref.current?.focus()}
      data-testid={testId}
    >
      {tags.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className="inline-flex items-center gap-1 bg-kb-subtle border border-kb-divider text-kb-ink px-2 py-0.5 rounded-full text-xs"
          data-testid={`${testId || "tags"}-chip-${i}`}
        >
          {t}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); remove(i); }}
            className="text-kb-ink-muted hover:text-kb-purple"
            aria-label={`Remove ${t}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        ref={ref}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => { if (draft.trim()) commit(draft); }}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData("text");
          if (pasted.includes(",")) {
            e.preventDefault();
            commit(draft ? `${draft},${pasted}` : pasted);
          }
        }}
        placeholder={tags.length ? "" : placeholder}
        className="flex-1 min-w-[120px] bg-transparent outline-none border-0 p-0 text-sm"
        data-testid={`${testId || "tags"}-input`}
      />
    </div>
  );
}
